import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"

// =====================
// Type definitions
// =====================

/** Paramètres pour la création d'un contact Pipedrive (POST). */
export type PipedriveCreateContactParams = {
    firstname: string
    lastname: string
    username: string
    phone: string
    activitySector: string | null
    product: string | null
    organization: string
}

/** Réponse API POST /v5/pipedrive/contact (create). */
export type PipedriveCreateContactResponse = {
    contact?: { id?: string; organization?: { id?: string } }
    deal?: { id?: string }
}

/** Paramètres pour la mise à jour d'un contact Pipedrive (PUT). */
export type PipedriveUpdateContactParams = {
    id: string | number
    dealId?: string | number
    firstname: string
    lastname: string
    username: string
    phone: string
    activitySector: string | null
    product: string | null
    organization: string
}

/** Réponse API PUT /v5/pipedrive/contact/:_id_contact (update). */
export type PipedriveUpdateContactResponse = {
    contact?: { id?: string }
    organization?: { id?: string }
    deal?: { id?: string }
}

/** Données Pipedrive à stocker dans paymentContext (ids). */
export type PipedriveContextIds = {
    contactId?: string
    organisationId?: string
    dealId?: string
}

// =====================
// Service
// =====================

/**
 * Service Pipedrive pour le parcours d'inscription (création/mise à jour contact).
 */
export class PipedriveService {
    /**
     * Crée un contact Pipedrive et une affaire pour une inscription.
     * POST /api/metier/v5/pipedrive/contact
     * @param params - firstname, lastname, username, phone, activitySector, product, organization
     * @returns Données contact, organisation et deal (ids)
     */
    async createContact(
        params: PipedriveCreateContactParams
    ): Promise<PipedriveCreateContactResponse> {
        return handleServiceCall<PipedriveCreateContactResponse>(async () => {
            const { data } = await axios.post<PipedriveCreateContactResponse>(
                "/api/metier/v5/pipedrive/contact",
                {
                    firstname: params.firstname,
                    lastname: params.lastname,
                    username: params.username,
                    phone: params.phone,
                    activitySector: params.activitySector ?? undefined,
                    product: params.product ?? undefined,
                    organization: params.organization,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "x-no-authorization": true,
                    },
                }
            )
            return data ?? {}
        })
    }

    /**
     * Met à jour un contact Pipedrive et son organisation/deal.
     * PUT /api/metier/v5/pipedrive/contact/:_id_contact
     * @param params - id, dealId, firstname, lastname, username, phone, activitySector, product, organization
     * @returns Données contact, organisation et deal (ids)
     */
    async updateContact(
        params: PipedriveUpdateContactParams
    ): Promise<PipedriveUpdateContactResponse> {
        const id = String(params.id)
        return handleServiceCall<PipedriveUpdateContactResponse>(async () => {
            const { data } = await axios.put<PipedriveUpdateContactResponse>(
                `/api/metier/v5/pipedrive/contact/${id}`,
                {
                    contact: {
                        name: `${params.firstname} ${params.lastname}`.trim(),
                        email: [
                            {
                                value: params.username,
                                primary: true,
                                label: "work",
                            },
                        ],
                        phone: [
                            {
                                value: params.phone,
                                primary: true,
                                label: "work",
                            },
                        ],
                    },
                    organization: {
                        name: params.organization,
                        activitySector: params.activitySector ?? undefined,
                    },
                    deal: {
                        id: params.dealId,
                        product: params.product ?? undefined,
                    },
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "x-no-authorization": true,
                    },
                }
            )
            return data ?? {}
        })
    }
}
